
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Navbar from "./components/Navbar";
import ScrollToTop from "@/components/routing/ScrollToTop";
import { Toaster } from "./components/ui/toaster";
import CookieConsent from "./components/CookieConsent";
import Footer from "./components/Footer";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Profile from "./pages/Profile";
import Projects from "./pages/Projects"; // Keep this import as-is
import Wallet from "./pages/Wallet";
import Marketplace from "./pages/Marketplace";
import Proposals from "./pages/Proposals";
import Reviews from "./pages/Reviews";
import Contracts from "./pages/Contracts";
import Posts from "./pages/Posts";
import Messages from "./pages/Messages";
import Interviews from "./pages/Interviews";
import TimeTracking from "./pages/TimeTracking";
import Income from "./pages/Income";
import Analytics from "./pages/Analytics";
import ROICalculator from "./pages/ROICalculator";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import JobSwipe from "./pages/JobSwipe";
import LikedJobs from "./pages/LikedJobs";
import AIJobMatching from "./pages/AIJobMatching";
import ProjectDetails from "./pages/ProjectDetails";
import Index from "./pages/Index";
import SkillsTest from "./pages/SkillsTest";
import { AuthGuard } from "./components/auth/AuthGuard";
import Client from "./pages/Client";
import Performance from "./pages/Performance";
import SubmitProject from "./pages/SubmitProject";
import Freelancers from "./pages/Freelancers";
import Agency from "./pages/Agency";
import AIAgents from "./pages/AIAgents";
import Enterprise from "./pages/Enterprise";
import EnterpriseSearch from "./pages/EnterpriseSearch";
import EnterpriseTalent from "./pages/EnterpriseTalent";
import TeamBuilder from "./pages/TeamBuilder";
import EnterpriseTraining from "./pages/EnterpriseTraining";
import FreelancerTraining from "./pages/FreelancerTraining";
import ProjectCollaborations from "./pages/ProjectCollaborations";
import DigitalPortfolio from "./pages/DigitalPortfolio";
import Documentation from "./pages/Documentation";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import CookiePolicy from "./pages/CookiePolicy";
import Security from "./pages/Security";
import GDPRCompliance from "./pages/GDPRCompliance";
import SupportCenter from "./pages/SupportCenter";
import Tutorials from "./pages/Tutorials";
import AboutUs from "./pages/AboutUs";
import Careers from "./pages/Careers";
import { ROUTES } from "./utils/routes";
import ForgotPassword from "./pages/ForgotPassword";
import MyJobs from "./pages/MyJobs";
import JobDetails from "./pages/JobDetails";
import TermsAndConditionContract from "./pages/TermsAndConditionContract";
import { useEffect, useState } from "react";
import { socket } from "./utils/axios";
import { useUserStore } from "./store/userStore";
import { useMessageStore } from './store/messageStore';
import { cn } from "./lib/utils";
import PaymentHistory from "./pages/PaymentHistory";
import TermsAndConditionFreelanceContract from "./pages/TermsAndConditionFreelanceContract";
import TermsAndConditionEntrepriseContract from "./pages/TermsAndConditionEntrepriseContract";

function App() {
  const { user, isAuthenticated} = useUserStore();
  const setUnreadCount = useMessageStore(state => state.setUnreadCount);

  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    const savedState = localStorage.getItem("sidebarCollapsed");
    if (savedState) {
      setIsCollapsed(savedState === "true");
    }
  }, []);

  useEffect(() => {
    if (user?.userId) {
      socket.connect();
      socket.emit("join", user.userId);

      socket.on("connect", () => {
        console.log("Socket connected:", socket.id);
      });

      socket.on("disconnect", () => {
        console.log("Socket disconnected");
      });

      socket.on('conversation:unreadCountUpdated', ({ conversationId, unreadCount }) => {
      setUnreadCount(conversationId, unreadCount);
    });
    }

    return () => {
      socket.disconnect();
      socket.off('conversation:unreadCountUpdated');
    };
  }, [user, setUnreadCount]);
  return (

	  <>
    {/* ✅ Popin RGPD globale, avant le Router */}
    <CookieConsent
      gtmId="GTM-WXH524ZW"
      brandGradient="linear-gradient(90deg,#16C784,#2F80ED)"
      brandBg="#0F172A"
      linkColor="#22D3EE"
    />
    <Router>
      <Navbar />
      <div className="flex">
        {isAuthenticated && <Sidebar /> }
        <main className={cn(
          "transition-all duration-300 flex-1 p-4 md:p-6",
          user && (isCollapsed ? "ml-16" : "ml-64")
        )}>
		{/* ✅ Remonte en haut à chaque changement de route / hash */}
        <ScrollToTop />
          <Routes>
            {/* Public routes */}
            <Route path={ROUTES.HOME} element={<Index />} />
            <Route path={ROUTES.AUTH} element={<Auth />} />
            <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPassword />} />
            <Route path={ROUTES.ENTERPRISE} element={<Enterprise />} />
            <Route path={ROUTES.ENTERPRISE_SEARCH} element={<EnterpriseSearch />} />
            <Route path={ROUTES.ENTERPRISE_TALENT} element={<EnterpriseTalent />} />
            <Route path={ROUTES.ENTERPRISE_TRAINING} element={<EnterpriseTraining />} />
            <Route path={ROUTES.DOCUMENTATION} element={<Documentation />} />
            <Route path={ROUTES.TUTORIALS} element={<Tutorials />} />
            <Route path={ROUTES.ABOUT_US} element={<AboutUs />} />
            <Route path={ROUTES.CAREERS} element={<Careers />} />
            <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicy />} />
            <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfService />} />
            <Route path={ROUTES.TERMS_OF_SERVICE_FREELANCER} element={<TermsAndConditionFreelanceContract />} />
            <Route path={ROUTES.TERMS_OF_SERVICE_ENTERPRISE} element={<TermsAndConditionEntrepriseContract />} />
            <Route path={ROUTES.COOKIE_POLICY} element={<CookiePolicy />} />
            <Route path={ROUTES.SECURITY} element={<Security />} />
            <Route path={ROUTES.GDPR_COMPLIANCE} element={<GDPRCompliance />} />
            <Route path={ROUTES.SUPPORT_CENTER} element={<SupportCenter />} />

            {/* Protected routes - only accessible when authenticated */}
            <Route path={ROUTES.DASHBOARD} element={<AuthGuard allowedRoles={["freelancer", "Enterprise"]}><Dashboard /></AuthGuard>} />
            <Route path={ROUTES.PROFILE} element={<AuthGuard allowedRoles={["freelancer", "Enterprise"]}><Profile /></AuthGuard>} />
            <Route path={ROUTES.PROJECTS} element={<AuthGuard allowedRoles={["freelancer"]}><Projects /></AuthGuard>} />
            <Route path={ROUTES.MYJOBS} element={<AuthGuard allowedRoles={["Enterprise"]}><MyJobs /></AuthGuard>} />
            <Route path={ROUTES.PROJECT_DETAILS(":id")} element={<AuthGuard allowedRoles={["freelancer"]}><ProjectDetails /></AuthGuard>} />
            <Route path={ROUTES.JOB_DETAILS(":id")} element={<AuthGuard allowedRoles={["Enterprise"]}><JobDetails /></AuthGuard>} />
            <Route path={ROUTES.PROJECT_COLLABORATIONS} element={<AuthGuard allowedRoles={["Enterprise"]}><ProjectCollaborations /></AuthGuard>} />
            <Route path={ROUTES.WALLET} element={<AuthGuard><Wallet /></AuthGuard>} />
            <Route path={ROUTES.PAYMENT_HISTORY} element={<AuthGuard><PaymentHistory /></AuthGuard>} />
            <Route path={ROUTES.MARKETPLACE} element={<AuthGuard><Marketplace /></AuthGuard>} />
            <Route path={ROUTES.PROPOSALS} element={<AuthGuard><Proposals /></AuthGuard>} />
            <Route path={ROUTES.REVIEWS} element={<AuthGuard><Reviews /></AuthGuard>} />
            <Route path={ROUTES.CONTRACTS} element={<AuthGuard><Contracts /></AuthGuard>} />
            <Route path={ROUTES.POSTS} element={<AuthGuard><Posts /></AuthGuard>} />
            <Route path={ROUTES.MESSAGES} element={<AuthGuard><Messages /></AuthGuard>} />
            <Route path={ROUTES.INTERVIEWS} element={<AuthGuard><Interviews /></AuthGuard>} />
            <Route path={ROUTES.TIME_TRACKING} element={<AuthGuard><TimeTracking /></AuthGuard>} />
            <Route path={ROUTES.INCOME} element={<AuthGuard><Income /></AuthGuard>} />
            <Route path={ROUTES.ANALYTICS} element={<AuthGuard><Analytics /></AuthGuard>} />
            <Route path={ROUTES.ROI_CALCULATOR} element={<AuthGuard><ROICalculator /></AuthGuard>} />
            <Route path={ROUTES.JOB_SWIPE} element={<AuthGuard><JobSwipe /></AuthGuard>} />
            <Route path={ROUTES.LIKED_JOBS} element={<AuthGuard><LikedJobs /></AuthGuard>} />
            <Route path={ROUTES.AI_JOB_MATCHING} element={<AuthGuard><AIJobMatching /></AuthGuard>} />
            <Route path={ROUTES.AI_AGENTS} element={<AuthGuard><AIAgents /></AuthGuard>} />
            <Route path={ROUTES.SKILLS_TEST} element={<AuthGuard><SkillsTest /></AuthGuard>} />
            <Route path={ROUTES.CLIENT} element={<AuthGuard><Client /></AuthGuard>} />
            <Route path={ROUTES.PERFORMANCE} element={<AuthGuard><Performance /></AuthGuard>} />
            <Route path={ROUTES.SUBMIT_PROJECT} element={<AuthGuard allowedRoles={["Enterprise"]}><SubmitProject /></AuthGuard>} />
            <Route path={ROUTES.FREELANCERS} element={<AuthGuard allowedRoles={["Enterprise"]}><Freelancers /></AuthGuard>} />
            <Route path={ROUTES.AGENCY} element={<AuthGuard><Agency /></AuthGuard>} />
            <Route path={ROUTES.TEAM_BUILDER} element={<AuthGuard><TeamBuilder /></AuthGuard>} />
            <Route path={ROUTES.FREELANCER_TRAINING} element={<AuthGuard><FreelancerTraining /></AuthGuard>} />
            <Route path={ROUTES.DIGITAL_PORTFOLIO} element={<AuthGuard><DigitalPortfolio /></AuthGuard>} />
            <Route path={ROUTES.TERMS_AND_CONDITION_CONTRACT} element={<AuthGuard allowedRoles={["freelancer"]}><TermsAndConditionContract /></AuthGuard>} />
            {/* Fallback */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
      <Footer />
      <Toaster />
    </Router>
	</>
  );
}

export default App;